import { useState } from "react";
import { Modal } from "components/core/basic/RACModal";
import { Row, Col, Badge, Table as ReadOnlyTable } from "react-bootstrap";
import { snakeCaseToSentenceCase } from "utils/formatText";
import AssetOverflowTooltip from "./AssetOverflowTooltip";
import { CREATIVE_VALIDATIONS, SB_BID_STRATEGY_OPTIONS } from "../AdPlan/constants";
import { AD_TYPES } from "../AdPlan/constants";
import ToolTips from "utils/toolTips";
import AdPlanModal from "../AdPlan/AdPlanModal";
import { useCategoriesQuery } from "hooks/useCategories";
import { useAppSelector } from "redux/hooks";
import { buttonRecipe } from "components/core/basic/Button";
import { getCampaignsByAdType } from "utils/getCampaignsByAdType";
import { Checkbox } from "../../../fields/base/Checkbox";
import { HStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";

const AssetCard = ({
  asset,
  onAssetSelection,
  isVideo,
  isSelected,
  allowExpandedModal,
  adType,
  assetTypeFilter,
  hideSelectionControls = false,
  isImageGenerationCard,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showExpandedIcon, setShowExpandedIcon] = useState(false);

  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);

  const { data: categoriesData } = useCategoriesQuery(marketPlace, channel.currentChannel);

  const {
    asset_id,
    name,
    url_default,
    tags,
    moderation_request_status,
    file_meta_data,
    approved_programs,
    failed_specifications,
    prompt,
    theme,
    asin,
  } = asset;

  const { width, height, sizeInBytes } = file_meta_data;
  const validations = CREATIVE_VALIDATIONS[adType];

  const getEligibleStatus = () => {
    let assetValidations = [];

    if (["LIFESTYLE_IMAGE", "PRODUCT_IMAGE"].includes(assetTypeFilter)) {
      assetValidations = validations?.lifestyleImage;
    }
    if (["LOGO"].includes(assetTypeFilter)) {
      assetValidations = validations?.logo;
    }
    const { minHeight, minWidth, maxFileSize } = assetValidations ?? {};

    if (width < minWidth || height < minHeight) {
      return [false, "Invalid asset dimensions"];
    }

    if (sizeInBytes > maxFileSize) {
      return [false, "Maximum allowed file size exceeded"];
    }

    // For video assets, check approved programs sent back from amazon
    if (isVideo) {
      if (adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) {
        return [
          approved_programs?.includes("SPONSORED_BRANDS_VIDEO"),
          failed_specifications
            ?.filter((reason) => reason.specProgramName === "SPONSORED_BRANDS_VIDEO")
            ?.map((reason) => reason.specifications)
            ?.flat()
            ?.map((spec) => spec.stringId),
        ];
      }

      if (adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
        return [
          approved_programs?.includes("SPONSORED_DISPLAY_VIDEO"),
          failed_specifications
            ?.filter((reason) => reason.specProgramName === "SPONSORED_DISPLAY_VIDEO")
            ?.map((reason) => reason.specifications)
            ?.flat()
            ?.map((spec) => spec.stringId),
        ];
      }
    }
    return [true, null];
  };

  const [isEligibleAsset, ineligibilityReasons] = getEligibleStatus();

  return (
    <>
      <Col xs={12} md={6} lg={3} className="p-2 m-0 asset-card">
        <div className="rounded-3 p-3 pb-5">
          <div
            onMouseEnter={() => {
              if (allowExpandedModal) {
                setShowExpandedIcon(true);
              }
            }}
            onMouseLeave={() => {
              if (allowExpandedModal) {
                setShowExpandedIcon(false);
              }
            }}
          >
            {isVideo ?
              <video controls className="w-100 asset-card-img" key={`video-${asset.asset_id}-${url_default}`}>
                <source src={url_default} type="video/mp4" />
              </video>
            : <img
                loading="lazy"
                src={url_default}
                alt={name}
                className={`w-100 asset-card-img ${isImageGenerationCard ? "h-100" : ""}`}
                onClick={() => setShowModal(true)}
                style={{ cursor: "pointer" }}
              />
            }
            {showExpandedIcon && (
              <span
                onClick={() => {
                  setShowModal(true);
                }}
                className="zoom-icon"
              >
                <i className="fa fa-expand" />
              </span>
            )}
          </div>

          <HStack justifyContent={"space-between"} maxWidth={"100%"} paddingY="xsmall">
            <AssetOverflowTooltip
              text={name}
              onClick={() => {
                setShowModal(true);
              }}
            />
            {!hideSelectionControls && (
              <Checkbox
                value={isSelected}
                isDisabled={!isEligibleAsset}
                onChange={() => {
                  onAssetSelection(asset);
                }}
              />
            )}
          </HStack>

          <Row className="d-flex align-items-center px-3">
            {moderation_request_status && moderation_request_status?.toLowerCase() !== "skipped" && (
              <Badge
                className="badge bg-trellis-purple w-auto m-1"
                style={{
                  borderRadius: "5px",
                }}
              >
                {snakeCaseToSentenceCase(moderation_request_status)}
              </Badge>
            )}

            {tags.map((tag) => {
              const isAsin = !!tag
                ?.toString()
                .toLowerCase()
                .match(/b0[a-z0-9]{8}/i);

              return (
                <Badge
                  key={tag}
                  className={`badge bg-trellis-${isImageGenerationCard ? "purple" : "fuchsia"} w-auto m-1`}
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  {isAsin ? tag.toUpperCase() : snakeCaseToSentenceCase(tag)}
                </Badge>
              );
            })}

            {!isEligibleAsset && (
              <>
                <Badge
                  className="badge bg-trellis-purple w-auto m-1"
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  Ineligible
                </Badge>{" "}
                {ineligibilityReasons && (
                  <ToolTips
                    toolTip={
                      typeof ineligibilityReasons === "string" ? ineligibilityReasons : (
                        `${ineligibilityReasons?.join(", ")}.`
                      )
                    }
                    position="top"
                    id={"ASSET_INELIGIBILITY_REASON"}
                    iconContainerClasses="d-flex w-auto text-decoration-none px-1"
                  />
                )}
              </>
            )}
            {isImageGenerationCard && (
              <div className="d-flex justify-content-center pt-4">
                <AdPlanModal
                  key={`assetId-${asset_id}`}
                  initialAdPlan={{
                    adType: AD_TYPES.SPONSORED_BRAND.value,
                    newProductCollection: true,
                    generatedAssetNameFilter: name,
                    name: `Gen AI SB ${asin}`,
                    bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                    campaignNames: getCampaignsByAdType(AD_TYPES.SPONSORED_BRAND.value, {}, false, `Gen AI SB ${asin}`),
                    storeLandingPage: true,
                    newLandingPage: false,
                    storeSpotlight: false,
                  }}
                  categories={categoriesData}
                  altModalButton={<button className={buttonRecipe({ size: "small" })}>Create a New Media Plan</button>}
                  assetShortcutModal={true}
                />
              </div>
            )}
          </Row>
        </div>
      </Col>
      <Modal
        isOpen={showModal}
        isDismissable={false}
        closeModal={() => setShowModal(false)}
        onOpenChange={(isOpen) => {
          setShowModal(isOpen);
        }}
        title={isVideo ? null : name}
        className={css({ width: "1200px", margin: 0, paddingTop: "50px" })}
      >
        <Row
          className="d-flex justify-content-center align-content-center"
          style={{
            height:
              isVideo ? "85vh"
              : isImageGenerationCard ? "68vh"
              : "75vh",
          }}
        >
          <Col xs={12} className={`${isImageGenerationCard ? "h-75" : "h-100"} pb-5`}>
            {isVideo ?
              <>
                <h4
                  className="title text-center mt-0"
                  style={{
                    marginBottom: "50px",
                    fontSize: "24px",
                    color: "#403E3D",
                    fontWeight: "700",
                  }}
                >
                  {name}
                </h4>
                <video controls className="w-100" key={`modal-video-${asset.asset_id}-${url_default}`}>
                  <source src={url_default} type="video/mp4" />
                </video>
              </>
            : <img
                loading="lazy"
                src={url_default}
                alt={name}
                className="w-100 h-100"
                style={{ objectFit: "contain" }}
              />
            }
          </Col>
          {isImageGenerationCard && prompt && theme.themeForDisplay ?
            <Col xs={12} className="px-5 py-3">
              <ReadOnlyTable borderless className="mx-5" style={{ maxWidth: "950px" }}>
                <tbody>
                  <tr>
                    <th className="d-flex">Prompt:</th>
                    <td>{prompt}</td>
                  </tr>
                  <tr>
                    <th className="">Theme:</th>
                    <td className="d-flex align-items-center justify-content-between w-100">
                      <span>{theme?.themeForDisplay}</span>
                      <AdPlanModal
                        key={`assetId-${asset_id}`}
                        initialAdPlan={{
                          adType: AD_TYPES.SPONSORED_BRAND.value,
                          newProductCollection: true,
                          generatedAssetNameFilter: name,
                          name: `Gen AI SB ${asin}`,
                          bidStrategy: SB_BID_STRATEGY_OPTIONS.MAXIMIZE_IMMEDIATE_SALES.value,
                          campaignNames: getCampaignsByAdType(
                            AD_TYPES.SPONSORED_BRAND.value,
                            {},
                            false,
                            `Gen AI SB ${asin}`,
                          ),
                          storeLandingPage: true,
                          newLandingPage: false,
                          storeSpotlight: false,
                        }}
                        categories={categoriesData}
                        altModalButton={
                          <button className={buttonRecipe({ size: "small" })}>Create a New Media Plan</button>
                        }
                        assetShortcutModal={true}
                      />
                    </td>
                  </tr>
                </tbody>
              </ReadOnlyTable>
            </Col>
          : null}
        </Row>
      </Modal>
    </>
  );
};

export default AssetCard;
